import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import {
    ComponentDefinition,
    DateOrder,
    Dictionary,
    DigitalSetSimpleDictionary,
    Group, IValueViewModel,
    Limit,
    TagStringValueDto,
    VariableType,
    WidgetValues
} from '../../../models';
import {
    TableByGroupWidget
} from '../tableByGroup.widget';
import {
    IRowViewModel,
    IdentifierRow,
    TableByGroupWidgetSeries
} from '..';
import {
    ITableByGroupService,
    IExportToCsvService,
    ILoggerService,
    IDateRangeService,
    ITableByGroupAggregation,
    IValueFormattingService,
    IStoreService,
    ISeriesService
} from '../../../services';
import {
    guidEmpty,
    partition,
    sortByOptionsTableByGroup,
    toDictionary
} from '../../../common';

class TableByGroupController implements angular.IComponentController, angular.IOnChanges {
    aggregateds: ITableByGroupAggregation[];
    allGroupsDictionary: Dictionary<Group>;
    hasUnits: boolean;
    identifiers: IdentifierRow[];
    isLoading: boolean;
    item: TableByGroupWidget;
    sorted: IRowViewModel[];

    readonly boundData: WidgetValues;
    readonly boundSeries: TableByGroupWidgetSeries[];
    readonly digitalSets: DigitalSetSimpleDictionary;
    readonly groups: Group[];
    readonly isBound: boolean;
    readonly referenceGroupGuid: string;
    readonly selectedGroup: Group;
    readonly stringValues: TagStringValueDto[];
    readonly viewModels: Dictionary<IValueViewModel>;

    private _variables: angular.IPromise<Dictionary<VariableType>>;
    private _values: IRowViewModel[];
    private _unsubscribe: () => void;

    static $inject = [
        '$scope',
        '$q',
        'tableByGroupService',
        'exportToCsvService',
        'loggerService',
        'dateRangeService',
        'valueFormattingService',
        'storeService',
        'seriesService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _tableByGroupService: ITableByGroupService,
        private _exportToCsvService: IExportToCsvService,
        private _loggerService: ILoggerService,
        private _dateRangeService: IDateRangeService,
        private _valueFormattingService: IValueFormattingService,
        private _storeService: IStoreService,
        private _serieService: ISeriesService,
    ) {
        this._variables = this._storeService.dispatchers().variables
            .getVariableTypes()
            .then(v => toDictionary(v));
    }

    $onInit() {
        this.identifiers = this.identifiers || [];
        this._values = this._values || [];

        this.refreshItemCurrentGroup();
        if (!this.isBound) {
            // Edit mode, setup watchers
            this.refreshSeries(this.item.configuration.series)
                .then(() => this.initWatchers());
        }
        this.item.exportToCsv = () => this.exportToCsv();
    }

    $onChanges(changes: angular.IOnChangesObject): void {
        if (changes['groups']?.currentValue) {
            this.allGroupsDictionary = toDictionary(this.groups);
        }

        if (changes['viewModels']?.currentValue || changes['groups']?.currentValue || changes['boundSeries']?.currentValue) {
            const series = this.item.configuration.series;
            this.refreshSeries(series);
        }

        if (changes['boundData']?.currentValue && this.boundData.guid) {
            this.prepareData(this.boundData);
        }
        
        if (changes['selectedGroup']?.currentValue) {
            this.item.selectedGroup = this.selectedGroup?.guid;
            this.refreshGroups(this.identifiers);
        }
        
        if (changes['referenceGroup']?.currentValue) {
            this.item.selectedGroup = this.referenceGroupGuid;
            this.refreshGroups(this.identifiers);
        }
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    getValueName(valueType: string): string {
        return this._tableByGroupService.getValueName(valueType);
    }

    // not my choice but widget (item) is used to transmit the guid of the current group
    // it is required to bind correctly group and series,
    // thus must be updated when item is changed, by example on save
    private refreshItemCurrentGroup() {
        this.item.selectedGroup = this.isBound
            ? this.selectedGroup?.guid
            : this.referenceGroupGuid;
    }

    private initWatchers() {
        this._$scope.$watch(
            () => this.item.configuration.series,
            newValue => {
                if (newValue) {
                    this.refreshSeries(newValue);
                }
            },
            true
        );

        this._$scope.$watchGroup(
            [
                () => this.item.configuration.groupTypes,
                () => this.item.configuration.groupGuidCollection,
                () => this.item.configuration.isBoundToGroup,
            ],
            () => this.refreshGroups(this.identifiers)
        );

        this._$scope.$watch(
            () => this.item.configuration.hasAlarm,
            () => this.refreshData()
        );

        this._$scope.$watch(
            () => this.item,
            () => this.refreshItemCurrentGroup()
        );
    }

    private exportToCsv() {
        this.isLoading = true;
        try {
            const configuration = this.item.configuration;
            const values = this._values;
            const dataArray = this._tableByGroupService
                .buildTable(configuration, values, this.aggregateds, this.identifiers);

            this._exportToCsvService.getCsvFromData(
                dataArray,
                `TableByGroup_${this.item.name}_${moment(this._dateRangeService.beginDate() as string).format('YYYYMMDD_HHmm')}.csv`
            );
        } catch (e) {
            this._loggerService.logError('Failed to export table by group to csv.');
            this._loggerService.logError(e.message);
        }
        this.isLoading = false;
    }

    private refreshSeries(series: TableByGroupWidgetSeries[]): angular.IPromise<IRowViewModel[]> {
        this.hasUnits = _.some(series, s => s.isUnitVisible);
        return this._$q
            .all([this._variables])
            .then(resolved => {
                const variables = resolved[0];
                // Full bound mode
                if (this.viewModels) {
                    const identifiers = this._tableByGroupService.getIdentifierRows(series, this.viewModels);
                    identifiers.forEach(i => i.unit = this._serieService.convertUnit(i.viewModel, this.item?.configuration?.options, variables));
                    this.identifiers = identifiers;
                    return this.refreshGroups(this.identifiers);
                }
                return this._$q.when<IRowViewModel[]>([]);
            });
    }

    private refreshGroups(identifiers: IdentifierRow[]): angular.IPromise<IRowViewModel[]> {
        if (identifiers?.length > 0) {
            this.isLoading = true;
            return this._tableByGroupService
                .getGroupViewModels(this.item.configuration, this.item.selectedGroup, identifiers)
                .then(data => {
                    this._values = data;
                    this.refreshData();
                    return data;
                })
                .finally(() => this.isLoading = false);
        }
        return this._$q.when([]);
    }

    private refreshData() {
        this.prepareData(this.boundData || {} as WidgetValues);
    }

    private prepareData(data: WidgetValues) {
        if (this._values?.length > 0) {
            this._values.forEach(g => {
                g.values.forEach(v => {
                    if (v.guid === guidEmpty) {
                        v.value = '-';
                    } else {
                        const found = _.find(data.aggregatedValue, d => d.guid === v.guid && d.valueType === v.valueType);
                        if (found && this.viewModels) {
                            const foundVvm = this.viewModels[v.guid];
                            const mode = this.item.configuration.aggregationMode;
                            v.dataType = foundVvm?.actualDataType;
                            v.value = this._valueFormattingService.formatValue(found, foundVvm, this.digitalSets, mode, v.decimals, v.format, this.stringValues);
                            v.actualValue = found.value;
                            const status = _.find(data.statuses, (s: any) => s.key === v.guid);
                            v.status = status ? status.value : 0;
                            const alarm = _.find(data.alarms, (s: any) => s.key === v.guid);
                            v.alarmStatus = alarm ? alarm.value : -1;
                        } else {
                            v.value = '-';
                            v.actualValue = null;
                            v.status = 2;
                            v.alarmStatus = -1;
                        }
                    }
                });
            });
            this.aggregateValues();
            this.sorted = this.filterAndSort([...this._values]);
        } else {
            this.aggregateds = [];
            this.sorted = [];
        }
    }

    private aggregateValues() {
        this.aggregateds  = this._tableByGroupService.aggregateValues(this._values, this.identifiers);
    }

    private filterAndSort(values: IRowViewModel[]): IRowViewModel[] {
        const { sortBy, limit, maxItems, sortOrder } = this.item.configuration;

        // prepare sort filter, ensure empty values are at the end #43655
        const sorter = sortOrder === DateOrder.Increasing
            ? (a, b) => (this.isEmpty(a.value) || (a.value > b.value)) ? 1 : -1
            : (a, b) => (this.isEmpty(a.value) || (a.value < b.value)) ? 1 : -1;

        // default selector on name
        let selector = g => ({ row: g, value: g.name, isEmpty: false });
        const fixedSortFields = sortByOptionsTableByGroup.map(sortBy => sortBy.key);
        if (fixedSortFields.indexOf(sortBy) >= 0) {
            selector = g => {
                return { row: g, value: g.original[sortBy], isEmpty: this.isEmpty(g.original[sortBy]) };
            };
        } else if (sortBy) {
            // find index of the original series in widget config
            const index = _.findIndex(this.item.configuration.series, s => s.guid + s.value === sortBy);
            // find matching value to sort for each group
            selector = g => {
                const val = g.values[index]?.actualValue;
                return { row: g, value: val, isEmpty: this.isEmpty(val) };
            };
        }

        // sort for each group
        const sorted = values
            .map(selector)
            .sort(sorter);

        const part = partition(sorted, v => v.isEmpty);
        const empty = part.keep;
        let nonEmpty = part.remove;

        if (maxItems > 0) {
            switch (limit) {
                case Limit.None:
                    break;
                case Limit.Top:
                    nonEmpty = nonEmpty.splice(0, maxItems);
                    break;
                case Limit.Bottom:
                    nonEmpty = nonEmpty
                        .reverse()
                        .splice(0, maxItems)
                        .reverse();
                    break;
            }
        }

        // If limit has not been reached by non-empty values, take some empty one if there is some
        if (limit !== Limit.None && empty.length > 0) {
            const emptyCount = maxItems - nonEmpty.length;
            for (let i = 0; i < emptyCount && empty.length > 0; i++) {
                nonEmpty.push(empty.splice(0, 1)[0]);
            }
        } else {
            // If there is no limit, take all
            nonEmpty.push(...empty);
        }

        return nonEmpty.map(a => a.row);
    }

    private isEmpty(value: unknown): boolean {
        return value === undefined
            || value === null
            || value === 'NaN';
    }
}

export const wtTableByGroup: ComponentDefinition = {
    componentId: 'wtTableByGroup',
    controller: TableByGroupController,
    template: require('./tableByGroup.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<?',
        boundSeries: '<?',
        digitalSets: '<',
        groups: '<',
        isBound: '<?',
        item: '=',
        referenceGroupGuid: '<?',
        selectedGroup: '<',
        stringValues: '<',
        viewModels: '<',
    }
};